import * as React from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import Checkbox from '@liveauctioneers/caterwaul-components/lib/Checkbox/Checkbox';
import classNames from 'classnames';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import debounce from 'lodash/debounce';
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Form from 'react-bootstrap/lib/Form';
import FormControl from 'react-bootstrap/lib/FormControl';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import isEqual from 'react-fast-compare';
import NoResults from './NoResults';
import Pagination from '@liveauctioneers/caterwaul-components/lib/Pagination/Pagination';
import { preventDefault } from '@/utils/preventDefault';
import SearchStatus from './SearchStatus';
import Select from '@liveauctioneers/caterwaul-components/lib/Select/Select';
import styled from 'styled-components';
import styles from './PaginateSortFilter.module.scss';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';

const VIEWS_TO_SHOW = [24, 48, 72, 96, 120, 'all'];

type Props = {
    buyNow: boolean;
    buyNowFilterAvailable: boolean;
    changePaginationPageSize: Function;
    children: React.ReactNode;
    className: string;
    empty: boolean;
    emptyMessage: React.ReactNode;
    filter?: string;
    filterOptions?: any;
    filteredItemCount: number;
    intl: IntlShape;
    isLoading: boolean;
    page: number;
    pageSize: number | 'all';
    pushQueryParam: Function;
    removeQueryParam: Function;
    replaceQueryParam: Function;
    searchTerms: string;
    showFilter?: boolean;
    sortOptions: any;
    sortOrder: string;
    throbberMessage: React.ReactNode;
    totalItemsCount: number;
    type: string;
    viewsToShow: any[];
};

class PaginateSortFilter extends React.Component<Props> {
    static defaultProps = {
        buyNow: false,
        buyNowFilterAvailable: false,
        className: '',
        empty: false,
        filter: 'won',
        isLoading: false,
        page: 1,
        searchTerms: '',
        showFilter: false,
        sortOptions: [],
        throbberMessage: '',
        totalItemsCount: 0,
        type: 'lots',
        viewsToShow: VIEWS_TO_SHOW,
    };

    constructor(props) {
        super(props);
        this.setSearch = debounce(this.setSearch, 300);
    }

    componentDidMount() {
        if (this.input && this.props.searchTerms !== this.input.value) {
            this.input.value = this.props.searchTerms;
            this.input.focus();
        }
    }

    shouldComponentUpdate = (nextProps: Props) => !isEqual(this.props, nextProps);

    componentDidUpdate() {
        if (this.input && this.props.searchTerms !== this.input.value) {
            this.input.value = this.props.searchTerms;
            this.input.focus();
        }
    }

    input: HTMLInputElement | null | undefined;

    handlePageSizeChange = ({ value }) => {
        this.changePageSize(value);
    };

    handleSearchChange = (event: any) => {
        this.setSearch(event.target.value);
    };

    handleSortChange = ({ value }) => {
        this.props.replaceQueryParam({ sort: value });
    };

    handleBuyNowChecked = (event: any) => {
        const buyNow = event.target.checked;
        if (buyNow) {
            this.props.replaceQueryParam({ buyNow });
        } else {
            this.props.removeQueryParam('buyNow');
        }
    };

    handleFilterChange = ({ value }) => {
        if (this.props.filter !== value) {
            this.props.pushQueryParam({ filter: value });
        }
    };

    changePage = (selected: number) => {
        const page = selected;
        if (selected === 0) {
            this.props.removeQueryParam('page');
        } else if (this.props.page !== page) {
            this.props.pushQueryParam({ page });
        }
    };

    changePageSize = (pageSize: number) => {
        // it's necessary to manually set the page size before navigating
        // because we need to read it off the cookie, not the params.
        // at some point this might enjoy a refactor, consolidating this
        // with the pattern used in the search page.
        this.props.changePaginationPageSize(pageSize);
        this.props.pushQueryParam({ pageSize });
    };

    onChange = ({ page, pageSize }) => {
        if (!Boolean(pageSize)) {
            this.changePage(page);
        } else {
            this.changePageSize(pageSize);
        }
    };

    setSearch = (value: string) => {
        if (value) {
            const search = value.toLowerCase().trim();
            this.props.replaceQueryParam({ terms: search });
        } else {
            this.props.removeQueryParam('terms');
        }
    };

    mapViewOptions = (option: string) => {
        const label = option === 'all' ? this.props.intl.formatMessage({ id: option }) : option;
        return { label, value: option };
    };

    mapSortByOptions = (option: string) => {
        const label = this.props.intl.formatMessage({ id: option.toLowerCase() });
        return { label, value: option };
    };

    mapFilterOptions = (option: string) => {
        if (!option) {
            return {};
        }
        const label = this.props.intl.formatMessage({ id: option.toLowerCase() });
        return { label, value: option };
    };

    clearSearch = () => {
        this.setSearch('');
        if (this.input) {
            this.input.value = '';
        }
    };

    preventDefault = preventDefault;

    render() {
        const {
            buyNow,
            buyNowFilterAvailable,
            children,
            className,
            empty,
            emptyMessage,
            filter,
            filteredItemCount,
            intl,
            isLoading,
            page,
            pageSize,
            searchTerms,
            showFilter,
            sortOptions,
            sortOrder,
            throbberMessage,
            totalItemsCount,
            type,
            viewsToShow,
        } = this.props;
        const { formatMessage, formatNumber } = intl;

        const viewOptions = viewsToShow.map(this.mapViewOptions);
        const sortByOptions = Object.keys(sortOptions).map(this.mapSortByOptions);
        const filterOptions = this.props.filterOptions
            ? Object.keys(this.props.filterOptions).map(this.mapFilterOptions)
            : [];
        const placeholderMessage = isLoading
            ? formatMessage({ id: 'loading' })
            : `${formatNumber(filteredItemCount)} ${type}`;

        return (
            <div className={`${styles['paginate-sort-filter']} ${className}`}>
                <SearchStatus clear={this.clearSearch} itemCount={filteredItemCount} search={searchTerms} />
                {empty && !isLoading && <NoResults>{emptyMessage}</NoResults>}
                {!empty && (
                    <Pagination
                        filter={
                            <Form className={styles.controls} inline onSubmit={this.preventDefault}>
                                <FormGroup
                                    className={classNames(styles.search, {
                                        [styles.extraWidth]: showFilter,
                                    })}
                                    controlId="search"
                                    validationState="success"
                                >
                                    <ControlLabel>
                                        <FormattedMessage id="search" />
                                        :&nbsp;
                                    </ControlLabel>
                                    <FormControl
                                        inputRef={(ref) => {
                                            this.input = ref;
                                        }}
                                        onChange={this.handleSearchChange}
                                        placeholder={placeholderMessage}
                                        type="search"
                                    />
                                    <FormControl.Feedback bsClass={styles['search-icon']}>
                                        <FontAwesomeIcon icon={faSearch} style={{ height: '14px', width: '14px' }} />
                                    </FormControl.Feedback>
                                </FormGroup>
                                <div className={styles.dropdownContainer}>
                                    {isLoading && (
                                        <div className={styles['my-bids-throbber']}>
                                            <Throbber className={styles.throbber} />
                                            {throbberMessage}
                                        </div>
                                    )}
                                    {buyNowFilterAvailable && (
                                        <Checkbox
                                            checked={buyNow}
                                            className={styles.checkbox}
                                            label={<FormattedMessage id="filter_buy_now" />}
                                            name="buyNowCheckbox"
                                            onChange={this.handleBuyNowChecked}
                                        />
                                    )}
                                    {showFilter && (
                                        <Select
                                            className={classNames(styles.selects, styles.bidstatus, styles.extraWidth)}
                                            hideSpacer
                                            label={
                                                <StyledLabel>
                                                    <FormattedMessage id="bid_status" />
                                                    :&nbsp;
                                                </StyledLabel>
                                            }
                                            labelLeft
                                            onChange={this.handleFilterChange}
                                            options={filterOptions}
                                            placeholder={formatMessage({ id: 'select' })}
                                            selectClassName={styles.bidStatusSelect}
                                            small
                                            value={filterOptions.find((op) => op.value === filter)}
                                        />
                                    )}
                                    <Select
                                        className={classNames(styles.selects, styles.view, {
                                            [styles.extraWidth]: showFilter,
                                        })}
                                        hideSpacer
                                        label={
                                            <StyledLabel>
                                                <FormattedMessage id="view" />:
                                            </StyledLabel>
                                        }
                                        labelLeft
                                        onChange={this.handlePageSizeChange}
                                        options={viewOptions}
                                        placeholder={formatMessage({ id: 'select' })}
                                        selectClassName={styles.pageSizeSelect}
                                        small
                                        value={viewOptions.find((op) => op.value === pageSize)}
                                    />
                                    <Select
                                        className={classNames(styles.selects, styles.sortby, {
                                            [styles.extraWidth]: showFilter,
                                        })}
                                        hideSpacer
                                        label={
                                            <StyledLabel>
                                                <FormattedMessage id="sort" />:
                                            </StyledLabel>
                                        }
                                        labelLeft
                                        onChange={this.handleSortChange}
                                        options={sortByOptions}
                                        placeholder={formatMessage({ id: 'select' })}
                                        selectClassName={styles.sortBySelect}
                                        small
                                        value={sortByOptions.find((op) => op.value === sortOrder)}
                                    />
                                </div>
                            </Form>
                        }
                        onChange={this.onChange}
                        pageSizeOptions={viewsToShow}
                        paginationFilter={{ page, pageSize, totalRecords: filteredItemCount || totalItemsCount }}
                    >
                        {!empty && <div className={styles['grid-wrapper']}>{children}</div>}
                    </Pagination>
                )}
            </div>
        );
    }
}

export default injectIntl(PaginateSortFilter);

const StyledLabel = styled.span`
    && {
        letter-spacing: 1px;
    }
`;
